import {
  getExcludedServerFormat,
  renderRepositorySelector,
  validateGlobPattern
} from "@/pages/settings/reviewer/helpers"
import { TagsInput } from "react-tag-input-component"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components"
import { CircleSlash, X } from "lucide-react"
import { RepoPathConfig, RepositoryOption } from "@/pages/settings/reviewer/defaultPrinciples/ExcludeConfigManager"
import { useSettings } from "@/services/store/useSettings"
import { useTranslation } from "react-i18next"
import { RepoPaths } from "@/models/ReviewerConfig"

interface Props {
  repositories: RepositoryOption[]
  onConfigChange: (config: RepoPaths | null) => void
  onConfigDelete: (repoName: string) => void
  disabled: boolean
  repoPathsConfigs: RepoPathConfig[]
  setRepoPathsConfigs: (newConfigs: RepoPathConfig[]) => void
}

export const RepoPathsSelector = ({
  repositories,
  onConfigChange,
  onConfigDelete,
  disabled,
  repoPathsConfigs,
  setRepoPathsConfigs
}: Props) => {
  const { selectedTheme } = useSettings()
  const { t } = useTranslation("Integrations")

  const handleToggle = (configId: string) => {
    const newConfigSections = repoPathsConfigs.map((config) =>
      config.id === configId ? { ...config, open: !config.open } : config
    )
    setRepoPathsConfigs(newConfigSections)
  }

  const handleSearchChange = (configId: string, value: React.SetStateAction<string>) => {
    const newConfigSections = repoPathsConfigs.map((config) =>
      config.id === configId
        ? {
            ...config,
            searchValue: typeof value === "string" ? value : value(config.searchValue)
          }
        : config
    )
    setRepoPathsConfigs(newConfigSections)
  }

  const handleSelect = (configId: string, value: string) => {
    const prevSelectedRepo = repoPathsConfigs.find((config) => config.id === configId)

    const newConfig = repoPathsConfigs.map((config) => {
      if (config.id === configId) {
        return {
          ...config,
          id: configId,
          selectedRepo: value,
          searchValue: "",
          open: false
        }
      }
      return config
    })

    onConfigChange(getExcludedServerFormat(newConfig, value))

    if (prevSelectedRepo && prevSelectedRepo.selectedRepo !== "") {
      onConfigDelete(prevSelectedRepo.selectedRepo)
    }

    setRepoPathsConfigs(newConfig)
  }

  const handlePathsChange = (configId: string, value: string[]) => {
    const currentConfigToUpdate = repoPathsConfigs.find((config) => config.id === configId)
    const newValue = value.join(",")
    const isValid = validateGlobPattern(newValue)

    const updatedConfigs = repoPathsConfigs.map((config) =>
      config.id === configId
        ? {
            ...config,
            selectedRepo: config.selectedRepo,
            paths: value,
            error: isValid ? "" : "Invalid glob pattern."
          }
        : config
    )

    if (isValid && currentConfigToUpdate) {
      onConfigChange(getExcludedServerFormat(updatedConfigs, currentConfigToUpdate.selectedRepo))
    }

    setRepoPathsConfigs(updatedConfigs)
  }

  const handleDelete = (repoName: string) => {
    const newRepoPathsConfigs = repoPathsConfigs.filter((config) => config.selectedRepo !== repoName)
    setRepoPathsConfigs(newRepoPathsConfigs)

    onConfigDelete(repoName)
  }

  return (
    <div>
      {repoPathsConfigs.map((config) => (
        <div className="flex">
          <div className="w-52">
            {renderRepositorySelector({
              config,
              repoPathsConfigs,
              repositories,
              handleSearchChange,
              handleToggle,
              handleSelect,
              disabled
            })}
          </div>
          <div className="flex grow">
            <div className="relative grow">
              <TagsInput
                value={config.paths.filter((value) => value !== "")}
                onChange={(value) => handlePathsChange(config.id, value)}
                name="paths"
                placeHolder={config.paths.length ? "" : t("EnterPath")}
                disabled={disabled}
                classNames={{
                  input: `${selectedTheme === "dark" ? "" : ""} h-[22px] rounded-none`,
                  tag: `${selectedTheme === "dark" ? "" : ""} p-0 px-1 h-5`
                }}
              />
              {config.error && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <CircleSlash className="text-red-500" />
                      </div>
                    </TooltipTrigger>

                    <TooltipContent>{config.error}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <button
              disabled={disabled}
              onClick={() => handleDelete(config.selectedRepo)}
              className={`ml-2 text-primary
              ${disabled ? "cursor-not-allowed" : ""}`}
              title="Delete configuration"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
